import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import UserList from '../components/AdminPanel/UserList';
import ModuleList from '../components/AdminPanel/ModuleList';
import { FederatedIdentity } from '../utils/AccessCredentials/FederatedIdentity';
import OrganizationUser from '../utils/OrganizationUser/OrganizationUser';
import { downloadFile } from '../utils/Helper/downloadFiles';
import Downloadfile from '../library/DownloadFiles/DownloadFile';
import AwsLogin from '../utils/AccessCredentials/AwsLogin';
import { AWS_SIGNIN_URL } from '../utils/Helper/helper';
import { IPPFourthLevelHeading, IPPHeading, IPPParagraph } from '../library/Heading/Heading';
import Button from '../library/Button/button';
import IUserList from '../utils/UserList/UserList.interface';
import ConditionalLoader from '../components/ConditionalLoader';
import newTabIcon from '../assets/img/newTabIcon.jpg';
import CopyIcon from '@rsuite/icons/Copy';
import { useNavigate } from 'react-router-dom';
import { routes } from '../routes/Routes';
import DeactiveAdmin from '../utils/Deactivate/DeactivateAdmin';
import { useAppSelector } from '../hooks/storeHooks';
import { useSessionRefresh } from '../hooks/useSessionRefresh';

const AdminPanel: React.FC = () => {
  const { data } = useAppSelector((state) => state.session);
  const [accessKey, setAccessKey] = useState<String>('');
  const [secret, setSecret] = useState<String>('');
  const [session, setSession] = useState<String>('');
  const [roleARN, setRoleArn] = useState<String>('');
  const [error, setError] = useState<string>(''); // New error state
  const [isFederatedSessionLoading, setIsFederatedSessionLoading] = useState<boolean>(false);
  const [openingCodeCommit, setOpeningCodeCommit] = useState<boolean>(false);
  const parsedLoginToken = JSON.parse(data.userData);
  const userPoolId = parsedLoginToken.userPoolId;
  const identityPoolId = parsedLoginToken.IdentityPoolId;
  const federatedIdentity = new FederatedIdentity({
    userPoolId,
    identityPoolId
  });
  const [isShowCred, setIsShowCred] = useState<boolean>(false);
  // Storing the user's name and displaying it as a welcome page
  const [userList, setUserList] = useState<Array<IUserList | null>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [dataUser, setDataUser]: any = useState([]);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [queryUser, setQueryUser] = useState('');
  const [copiedCred, setCopiedCred] = useState<boolean>(false);
  const navigate = useNavigate();
  const [showCredentialErrorBanner, setShowCredentialErrorBanner] = useState(false);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const pollingWindowTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingRestartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Download UserGuideBook, Factory Validation GuideBook and script.
  const downloadUserGuide = () => {
    const fileUrl = '/UserGuideBook.pdf';
    const fileName = 'UserGuideBook'; // Set the desired file name
    downloadFile(fileUrl, fileName);
  };
  const downloadFactoryValidation = () => {
    const fileUrl = '/FactoryValidationTestsSetup.pdf';
    const fileName = 'FactoryValidationTestsSetup'; // Set the desired file name
    downloadFile(fileUrl, fileName);
  };
  const downloadFileList = [
    { fileUrl: '/setup.sh', fileName: 'setup.sh' },
    { fileUrl: '/ipp_update_script.sh', fileName: 'ipp_update_script.sh' }
  ];
  const downloadScript = () => {
    for (const file of downloadFileList) {
      downloadFile(file.fileUrl, file.fileName);
    }
  };

  const userName = sessionStorage.getItem('adminFullName') ?? '';
  const orgEmailUpdated = sessionStorage.getItem('userEmail');

  const emailToUse = orgEmailUpdated ?? '';
  const { refreshSessionAndGetCredentials } = useSessionRefresh({
    userPoolId,
    identityPoolId,
    email: emailToUse
  });

  // This function is used to fetch federated session from backend and save it in component state
  const fetchFederatedSession = async () => {
    setIsFederatedSessionLoading(true);
    try {
      const idToken = parsedLoginToken.cognitoDetails.message.idToken.jwtToken;
      const role =
        parsedLoginToken.cognitoDetails.message.idToken.payload['cognito:preferred_role'];
      const responseData = await federatedIdentity.fetchAWSCredentials(idToken, emailToUse, role);
      const creds = responseData?.body?.data?.Credentials;
      if (!creds) {
        throw new Error('Missing AWS credentials in response');
      }
      setAccessKey(creds.AccessKeyId ?? '');
      setSecret(creds.SecretAccessKey ?? '');
      setSession(creds.SessionToken ?? '');
      setRoleArn(role);
      setIsShowCred(true);
    } catch (error: unknown) {
      if (
        (error instanceof Error && error.message === 'Missing AWS credentials in response') ||
        (typeof error === 'object' &&
          error !== null &&
          'statusCode' in error &&
          (error as { statusCode?: number }).statusCode === 403)
      ) {
        try {
          const creds = await refreshSessionAndGetCredentials();
          if (!creds) {
            setShowCredentialErrorBanner(true);
            return;
          }
          setAccessKey(creds.AccessKeyId ?? '');
          setSecret(creds.SecretAccessKey ?? '');
          setSession(creds.SessionToken ?? '');
          setIsShowCred(true);
        } catch {
          setShowCredentialErrorBanner(true);
        }
      } else if (error instanceof Error) {
        setError('JSON parsing error: ' + error.message);
      } else {
        setError('Error in aws console login: ' + error);
      }
    }
    setIsFederatedSessionLoading(false);
    setCopiedCred(false);
  };

  // this function is used to get sign-in token from backend and send it to aws federetion endpoint for console login.
  const getCredentials = async () => {
    setOpeningCodeCommit(true);
    try {
      const idToken = parsedLoginToken.cognitoDetails.message.idToken.jwtToken;
      const role =
        parsedLoginToken.cognitoDetails.message.idToken.payload['cognito:preferred_role'];
      const responseData = await federatedIdentity.fetchAWSCredentials(idToken, emailToUse, role);
      const creds = responseData?.body?.data?.Credentials;
      if (!creds) {
        throw new Error('Missing AWS credentials in response');
      }
      const awsLoginObj = new AwsLogin({
        accessKey: creds.AccessKeyId,
        secret: creds.SecretAccessKey,
        session: creds.SessionToken
      });
      const consolLoginStatus = await awsLoginObj.consoleLogin();
      if (consolLoginStatus.statusCode === 201) {
        const SigninToken = consolLoginStatus.body.SigninToken;
        const getLoginUrl = `${AWS_SIGNIN_URL}${SigninToken}`;
        window.open(getLoginUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error: unknown) {
      if (
        (error instanceof Error && error.message === 'Missing AWS credentials in response') ||
        (typeof error === 'object' &&
          error !== null &&
          'statusCode' in error &&
          (error as { statusCode?: number }).statusCode === 403)
      ) {
        try {
          const creds = await refreshSessionAndGetCredentials();
          if (!creds) {
            setShowCredentialErrorBanner(true);
            return;
          }
          const awsLoginObj = new AwsLogin({
            accessKey: creds.AccessKeyId,
            secret: creds.SecretAccessKey,
            session: creds.SessionToken
          });
          const consolLoginStatus = await awsLoginObj.consoleLogin();
          if (consolLoginStatus.statusCode === 201) {
            window.open(
              `${AWS_SIGNIN_URL}${consolLoginStatus.body.SigninToken}`,
              '_blank',
              'noopener,noreferrer'
            );
          }
        } catch {
          setShowCredentialErrorBanner(true);
        }
      } else if (error instanceof Error) {
        setError('JSON parsing error: ' + error.message);
      } else {
        setError('Error in aws console login: ' + error);
      }
    }
    setOpeningCodeCommit(false);
  };

  const handleEditUser = (user: IUserList) => {
    setDataUser((prvs: any) => dataUser.map((u: IUserList) => (u.email === user.email ? user : u)));
  };

  const handleDeleteUser = (email: string) => {
    const updatedDataUser = dataUser.filter((user: { email: any }) => user.email !== email);
    setDataUser(updatedDataUser);
  };

  const stopAllPolling = () => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (pollingWindowTimeoutRef.current) {
      clearTimeout(pollingWindowTimeoutRef.current);
      pollingWindowTimeoutRef.current = null;
    }
  };

  const scheduleNextPollingWindow = () => {
    if (pollingRestartTimeoutRef.current) {
      clearTimeout(pollingRestartTimeoutRef.current);
    }

    // Wait before checking again (60 seconds)
    pollingRestartTimeoutRef.current = setTimeout(() => {
      startShortPollingWindow();
    }, 60000);
  };

  const startShortPollingWindow = () => {
    // Start polling every 5 seconds
    stopAllPolling(); // cleanup any existing polling
    intervalIdRef.current = setInterval(() => {
      void getAdminUsers();
    }, 5000);

    // Stop polling after 5 seconds (one window)
    pollingWindowTimeoutRef.current = setTimeout(() => {
      stopAllPolling();
      scheduleNextPollingWindow();
    }, 5000);
  };

  // *********** UseEffect to handle delete user and create user *********** //
  const getAdminUsers = async () => {
    const organizationAdminObj = new OrganizationUser();
    const { users, admin } = await organizationAdminObj.getAllAdminUser();
    const currentIsActive = admin?.isActive ?? null;

    if (currentIsActive === false) {
      setShowCredentialErrorBanner(true);
      stopAllPolling(); // Stop all polling immediately
      if (pollingRestartTimeoutRef.current) {
        clearTimeout(pollingRestartTimeoutRef.current);
        pollingRestartTimeoutRef.current = null;
      }
    } else if (currentIsActive === true) {
      setShowCredentialErrorBanner(false);
    }

    setDataUser(users);
    setIsLoadingUser(false);
  };

  useEffect(() => {
    const lastLogoutStr = localStorage.getItem('adminLastLogoutTime');
    if (!lastLogoutStr) return;

    const lastLogout = new Date(lastLogoutStr);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - lastLogout.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= 90) {
      // Deactivate admin and block login
      const deactivateAdmin = async () => {
        try {
          const organizationAdminObj = new OrganizationUser();
          const { admin } = await organizationAdminObj.getAllAdminUser();
          if (admin?.isActive && admin.email) {
            const userObj = new DeactiveAdmin({
              email: admin.email,
              isActive: false
            });
            await userObj.deactivateAdmin();
            setShowCredentialErrorBanner(true);
          }
        } catch (error) {
          console.error('Failed to deactivate admin:', error);
        }
      };
      void deactivateAdmin();
    }
  }, []);

  useEffect(() => {
    startShortPollingWindow(); // Begin initial polling window on mount

    return () => {
      // Cleanup on unmount
      stopAllPolling();
      if (pollingRestartTimeoutRef.current) {
        clearTimeout(pollingRestartTimeoutRef.current);
      }
    };
  }, []);

  const copyCredentials = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    await navigator.clipboard.writeText(`aws_access_key_id: ${accessKey}
aws_secret_access_key: ${secret}
aws_session_token: ${session}`);
    setCopiedCred(true);
    setTimeout(() => {
      setCopiedCred(false);
    }, 1000);
  };

  // Fetching data from db and mapping it to the array. Here the promise returned is void.
  useEffect(() => {
    const getAdminUsersModules = async () => {
      const organizationAdminObj = new OrganizationUser();
      const result = await organizationAdminObj.getAllAdminUserModule();
      setUserList(result);
      setIsLoading(false);
    };
    void getAdminUsersModules();
  }, []);

  return (
    <div>
      <div className="overflow-hidden relative block">
        {showCredentialErrorBanner && (
          <div
            className="h-[56px] fixed top-0 left-0 w-full bg-[#fefbf7] text-black text-center py-3 px-6 z-50 rounded shadow cursor-pointer transition-all duration-300"
            onClick={() => {
              sessionStorage.clear();
              localStorage.clear();
              void navigate(routes.login);
              setShowCredentialErrorBanner(false);
            }}>
            <b>⚠️ Admin is deactivated. Click here to re-login</b>.
          </div>
        )}
      </div>
      <div
        className={`overflow-hidden relative block ${
          showCredentialErrorBanner ? 'pointer-events-none opacity-50 cursor-not-allowed' : ''
        }`}>
        <div className="bg-offwhite rounded-md shadow-md py-6 relative my-6">
          {/* <IPPHeading
          headerText={'Local Development Setup'}
          className={'w-fit text-black text-lg font-medium border-b-2 pt-2 pb-2 pl-6 pr-2'}
        /> */}
          <div className="max-w-full flex flex-wrap gap-4 pt-4 pl-4">
            <div className="max-w-full flex-grow-0 flex gap-4">
              <Button
                onclick={fetchFederatedSession}
                className="h-fit w-fit bg-transparent font-semibold disabled:cursor-progress hover:bg-black border rounded hover:text-white py-2 px-6"
                buttonText="Get AWS Cred."
                disabled={isFederatedSessionLoading}
              />
              <button
                className="h-fit w-fit relative bg-transparent border hover:bg-gradient-to-t from-light-gray to-gray rounded font-semibold py-2 pl-2 pr-6"
                onClick={getCredentials}
                disabled={openingCodeCommit}>
                {openingCodeCommit ? 'Please Wait...' : 'View in CodeCommit'}
                <img className="absolute top-1 right-1 h-4 w-4" src={newTabIcon} />
              </button>
            </div>
            <div className="flex-grow-0">
              <Downloadfile
                onGuideBook={downloadUserGuide}
                onScript={downloadScript}
                onFactoryTest={downloadFactoryValidation}
              />
            </div>
          </div>
          <ConditionalLoader isLoading={isFederatedSessionLoading}>
            {isShowCred && (
              <div className="max-w-full pt-2 px-4">
                <div className="p-4 mb-2 shadow-inner border border-light-gray rounded-md relative overflow-hidden">
                  <div className="w-full flex justify-end">
                    <button
                      className="text-black transition-all ease-in-out duration-100 hover:scale-105 shadow-sm border border-gray shadow-gray px-2 rounded-md -mr-2 -mt-1"
                      onClick={copyCredentials}
                      disabled={copiedCred}>
                      {copiedCred ? (
                        <small> Copied ✅</small>
                      ) : (
                        <small>
                          <CopyIcon className="h-3 w-3" /> Copy
                        </small>
                      )}
                    </button>
                  </div>
                  <IPPFourthLevelHeading
                    id="aceeskeyheader"
                    headerText={`aws_access_key_id: ${accessKey}`}
                    className={'text-black text-[16px] mb-0.5 leading-[1.2]'}
                  />
                  <IPPFourthLevelHeading
                    id="aceeskeyheader"
                    headerText={`aws_secret_access_key: ${secret}`}
                    className={'text-black text-[16px] mb-0.5 leading-[1.2]'}
                  />
                  <IPPParagraph
                    id="aceeskeyheader"
                    headerText={`aws_session_token: ${session}`}
                    className={'text-black text-[16px] mb-0.5 leading-[1.2] break-words'}
                  />
                </div>
                <div className="p-4 mb-2 shadow-inner border border-light-gray rounded-md">
                  <IPPFourthLevelHeading
                    id="aceeskeyheader"
                    headerText={`role_arn: ${roleARN}`}
                    className={'text-black text-[16px] mb-0.5 leading-[1.2]'}
                  />
                </div>
              </div>
            )}
          </ConditionalLoader>
        </div>
        <div className="flex flex-wrap">
          <div className="w-full md:flex lg:flex flex-row justify-start lg:mt-18 overflow-x-scroll 3xl:w-2/5 3xl:pr-4 my-8">
            <UserList
              isLoading={isLoadingUser}
              query={queryUser}
              setQuery={setQueryUser}
              users={dataUser}
              email={emailToUse}
              partnerName={userName}
              onDeleteUser={handleDeleteUser}
              onUserCreated={getAdminUsers}
              onEditUser={handleEditUser}
              getAdminUser={getAdminUsers}
            />
          </div>
          <div className="md:flex lg:flex w-full 3xl:w-3/5 flex-row justify-start shadow-md bg-offwhite rounded-md my-6">
            <ModuleList data={userList} isLoading={isLoading} query={query} setQuery={setQuery} />
          </div>
        </div>
      </div>
    </div>
  );
};
export default AdminPanel;
